import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, FileText, Loader2, Star, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import VoteButtons from "@/components/VoteButtons";
import ShareButtons from "@/components/ShareButtons";

const FILTERS = [
  { key: "", label: "All" },
  { key: "video", label: "Vlogs" },
  { key: "article", label: "Articles" },
];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/blog", { params: type ? { type } : {} })
      .then(({ data }) => setPosts(data))
      .finally(() => setLoading(false));
  }, [type]);

  return (
    <div className="min-h-screen bg-[#060810]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="mb-10">
          <p className="text-xs font-mono-caption uppercase tracking-[0.3em] text-cyan-400 mb-3">The Carivisor Journal</p>
          <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Stories & <span className="text-gradient-warm">Vlogs</span>
          </h1>
          <p className="text-slate-400 text-base mt-4 max-w-2xl">
            Dispatches, guides, and video adventures from across the Caribbean — straight from the Carivisor team.
          </p>
        </div>

        <div className="flex gap-2 mb-10" data-testid="blog-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`blog-filter-${f.label.toLowerCase()}`}
              onClick={() => setType(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                type === f.key ? "btn-neon" : "glass text-slate-300 hover:text-cyan-400"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
        ) : posts.length === 0 ? (
          <p className="text-slate-500 text-center py-24">No posts yet. Check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.id}`}
                data-testid={`blog-card-${p.id}`}
                className="group glass glass-hover rounded-2xl overflow-hidden flex flex-col"
              >
                <div className="relative aspect-[16/9] overflow-hidden bg-[#0B1020]">
                  {p.cover_image && (
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#070A18] via-transparent to-transparent opacity-70" />
                  <span className="absolute top-3 left-3 text-[10px] font-mono-caption uppercase tracking-widest px-3 py-1 rounded-full glass border border-cyan-300/30 text-cyan-300 flex items-center gap-1">
                    {p.type === "video" ? <><Play className="w-3 h-3" /> Vlog</> : <><FileText className="w-3 h-3" /> Article</>}
                  </span>
                  {p.type === "video" && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-[#ff5e00]/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Play className="w-6 h-6 text-white fill-white ml-1" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-heading font-extrabold text-white text-xl leading-tight mb-2 group-hover:text-gradient-warm line-clamp-2">
                    {p.title}
                  </h3>
                  <p className="text-sm text-slate-400 line-clamp-2 flex-1">{p.excerpt}</p>
                  <div className="flex items-center gap-4 pt-4 mt-4 border-t border-[#232A4D] text-xs text-slate-400">
                    <span className="flex items-center gap-1 text-emerald-400"><Star className="w-3.5 h-3.5" /> {p.score}</span>
                    <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {p.comments_count || 0}</span>
                    <span className="ml-auto text-slate-500">{p.author_name}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
